import { PlusOutlined } from "@ant-design/icons";
import { Image as ImageAntd, Upload } from "antd";
import { useEffect, useState } from "react";
import { MdDelete } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import { exceedFiles, imagesTypes, MAX_FILE_SIZE } from "../../config/Constant";
import { useUploadFileStore } from "../../store/UploadFileStore";
import { useInfoUser } from "../../store/UserStore";
import { getFileIcon } from "../../utils/SendFile";
import { openNotificationError } from "../../utils/Utils";
import "./style.scss";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const FilesPreview = ({ formData, setFormData, isOnlyView = false }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const { languageMap, user } = useInfoUser();
  const { uploadFiles, setUploadFiles } = useUploadFileStore();

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = (event) => {
    if (formData?.files?.length > exceedFiles) {
      openNotificationError(
        languageMap?.["error.exceed.fileNumber"] ??
          "The number of files exceeds 24",
      );
      return;
    }

    if (event?.file?.size === 0) {
      openNotificationError("Invalid file (file size = 0)");
      return;
    }

    const status = event?.file?.status;

    if (status === "removed") {
      const text = formData?.text;

      const newFiles = formData?.files.filter(
        (file) => file.uid !== event?.file?.uid,
      );
      const newMediaFiles = formData?.mediaFiles.filter(
        (file) => file.uid !== event?.file?.uid,
      );
      const newEncodePhotos = formData?.encodePhotos.filter(
        (file) => file.uid !== event?.file?.uid,
      );
      setFormData({
        text: text,
        files: newFiles,
        mediaFiles: newMediaFiles,
        encodePhotos: newEncodePhotos,
      });
    }

    if (status === "uploading") {
      const text = formData?.text;
      const files = [];
      const mediaFiles = [];
      const encodePhotos = [];

      if (formData?.files?.find((file) => file?.uid === event?.file?.uid))
        return;

      const originFile = event?.file?.originFileObj;

      if (
        originFile?.webkitRelativePath &&
        originFile.webkitRelativePath.includes("/")
      ) {
        openNotificationError(
          languageMap?.["error.not.sendFolder"] ?? "You can not send folder",
        );
        return;
      }

      if (event?.file?.size > MAX_FILE_SIZE) {
        openNotificationError("File size exceeds the 1GB limit");
        return;
      }

      originFile.uid = uuidv4();
      files.push(originFile);

      files.forEach((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = (r) => {
          const processFile = (file) => {
            return new Promise((resolve) => {
              if (imagesTypes.includes(file?.type)) {
                const img = new Image();
                img.src = r?.target?.result;

                img.onload = () => {
                  file.width = img.width;
                  file.height = img.height;
                  resolve();
                };
              } else {
                mediaFiles.push(file);
                resolve();
              }
            });
          };

          Promise.all(files.map(processFile)).then(() => {
            setFormData((prevFormData) => ({
              text: text,
              files: [...(prevFormData?.files || []), ...files],
              mediaFiles: [...(prevFormData?.mediaFiles || []), ...mediaFiles],
              encodePhotos: [
                ...(prevFormData?.encodePhotos || []),
                ...encodePhotos,
              ],
            }));

            setUploadFiles([...uploadFiles, ...(mediaFiles || [])]);
          });
        };
      });
    }
  };

  const handleDelete = (uid) => {
    const newFiles = formData?.files?.filter((file) => file.uid !== uid);
    const newMediaFiles = formData?.mediaFiles?.filter(
      (file) => file.uid !== uid,
    );
    const newEncodePhotos = formData?.encodePhotos?.filter(
      (file) => file.uid !== uid,
    );

    setFormData((prevState) => ({
      ...prevState,
      files: newFiles,
      mediaFiles: newMediaFiles,
      encodePhotos: newEncodePhotos,
    }));

    setFileList(newFiles);

    const newUploadFilesV2 = uploadFiles?.filter((file) => file?.uid !== uid);
    setUploadFiles([...(newUploadFilesV2 || [])]);
  };

  const removeFormData = () => {
    setFormData({});
    setFileList([]);

    const removedUuids = formData?.mediaFiles?.map((file) => file?.uid);
    const newUploadFiles = uploadFiles?.filter(
      (file) => !removedUuids?.includes(file?.uid),
    );
    setUploadFiles([...(newUploadFiles || [])]);
  };

  const beforeUpload = (file) => {
    const isLt1GB = file.size / 1024 / 1024 < 1024;
    if (!isLt1GB) {
      openNotificationError("File size exceeds the 1gb limit");
    }
    return isLt1GB;
  };

  const handleDragOver = (e) => {
    // Prevent the default behavior to avoid file duplication
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    if (formData?.files?.length > 0) {
      const files = formData?.files?.map((file) => {
        const isImage = ["image/jpeg", "image/png", "image/x-icon"].includes(
          file?.type,
        );
        return {
          uid: file?.uid,
          name: file?.name,
          status: URL.createObjectURL(file) ? "done" : "error",
          url: isImage ? URL.createObjectURL(file) : null,
          isImage: isImage,
        };
      });
      setFileList(files);
    }

    return () => {
      setFileList([]);
    };
  }, [formData?.files]);

  return (
    formData?.files?.length > 0 && (
      <div className="flex flex-col m-2 mt-0">
        <div className="flex justify-between">
          <div className="font-bold">
            {formData?.files?.length}{" "}
            {languageMap?.["filePreview.fileTotal"] ?? "file"}
            {user?.language === "us" && formData?.files?.length > 1 && "s"}
          </div>
          <button className="remove-all" onClick={removeFormData}>
            {languageMap?.["filePreview.removeAll"] ?? "Remove all"}
          </button>
        </div>
        <div
          className="flex max-h-[190px] overflow-y-scroll mt-[10px] gap-[10px]"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <Upload
            beforeUpload={beforeUpload}
            listType="picture-card"
            fileList={fileList}
            onPreview={handlePreview}
            onChange={handleChange}
            multiple
            itemRender={(originNode, file) => {
              return (
                <div className="custom-thumbnail">
                  {file.isImage ? (
                    originNode
                  ) : (
                    <div className="file-thumbnail">
                      {getFileIcon(file.name)}
                      <span>
                        {file.name.length > 10
                          ? file.name.substring(0, 10) + "..."
                          : file.name}
                      </span>
                      <button
                        className="delete-button"
                        onClick={() => handleDelete(file.uid)}
                      >
                        <MdDelete color="red" size={20} />
                      </button>
                    </div>
                  )}
                </div>
              );
            }}
          >
            <button style={{ border: 0, background: "none" }} type="button">
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>
                {languageMap?.["modal.conversation.upload"] ?? "Upload"}
              </div>
            </button>
          </Upload>
          {previewImage && (
            <ImageAntd
              wrapperStyle={{
                display: "none",
              }}
              preview={{
                visible: previewOpen,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(""),
              }}
              src={previewImage}
            />
          )}
        </div>
      </div>
    )
  );
};

export { FilesPreview };
